import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtMoney, fmtDateTime } from "@/lib/format";
import { Plus, MagnifyingGlass, PencilSimple, Trash, ArrowsDownUp } from "@phosphor-icons/react";

const emptyPart = { name: "", sku: "", category: "", unit: "шт", quantity: 0, min_quantity: 0, cost: 0, cost_currency: "UAH", price: 0, price_currency: "UAH", supplier_id: "" };
const emptySup = { name: "", phone: "", contact: "" };
const CUR_SYM = { UAH: "₴", USD: "$", EUR: "€" };

export default function Inventory() {
  const [parts, setParts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [movements, setMovements] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPart);
  const [editId, setEditId] = useState(null);
  const [mvOpen, setMvOpen] = useState(false);
  const [mv, setMv] = useState({ part_id: "", type: "in", quantity: 1, reason: "" });
  const [supOpen, setSupOpen] = useState(false);
  const [sup, setSup] = useState(emptySup);
  const [catFilter, setCatFilter] = useState("all");
  const [catOpen, setCatOpen] = useState(false);

  const categories = [...new Set(parts.map((p) => p.category).filter(Boolean))].sort();
  const visibleParts = catFilter === "all" ? parts : parts.filter((p) => p.category === catFilter);

  const loadParts = () => api.get("/parts", { params: { search } }).then((r) => setParts(r.data));
  const loadSup = () => api.get("/suppliers").then((r) => setSuppliers(r.data));
  const loadMv = () => api.get("/stock-movements").then((r) => setMovements(r.data));
  useEffect(() => { loadParts(); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => { loadSup(); loadMv(); }, []);

  const savePart = async () => {
    if (!form.name) return toast.error("Вкажіть назву");
    const payload = { ...form, quantity: Number(form.quantity), min_quantity: Number(form.min_quantity), cost: Number(form.cost), price: Number(form.price), cost_currency: form.cost_currency || "UAH", price_currency: form.price_currency || "UAH" };
    try {
      if (editId) await api.put(`/parts/${editId}`, payload);
      else await api.post("/parts", payload);
      toast.success("Збережено"); setOpen(false); setForm(emptyPart); setEditId(null); loadParts();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const editPart = (p) => { setForm({ ...p }); setEditId(p.id); setOpen(true); };
  const delPart = async (id) => { if (!window.confirm("Видалити?")) return; await api.delete(`/parts/${id}`); loadParts(); };

  const doMovement = async () => {
    try {
      await api.post(`/parts/${mv.part_id}/movement`, { type: mv.type, quantity: Number(mv.quantity), reason: mv.reason });
      toast.success(mv.type === "in" ? "Приймання проведено" : "Списання проведено");
      setMvOpen(false); setMv({ part_id: "", type: "in", quantity: 1, reason: "" }); loadParts(); loadMv();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const saveSup = async () => {
    if (!sup.name) return toast.error("Вкажіть назву");
    await api.post("/suppliers", sup); toast.success("Постачальника додано"); setSupOpen(false); setSup(emptySup); loadSup();
  };

  const F = (k, label, type = "text") => (
    <div><Label>{label}</Label><Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`part-${k}`} /></div>
  );

  return (
    <div>
      <PageHeader title="Склад" subtitle="Запчастини, постачальники, рух" />
      <Tabs defaultValue="parts">
        <TabsList className="mb-4">
          <TabsTrigger value="parts" data-testid="tab-parts">Запчастини</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Постачальники</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Рух складу</TabsTrigger>
        </TabsList>

        <TabsContent value="parts">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="relative max-w-sm flex-1 min-w-[200px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Пошук запчастини..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="parts-search" />
            </div>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-52" data-testid="category-filter"><SelectValue placeholder="Категорія" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всі категорії</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Dialog open={mvOpen} onOpenChange={setMvOpen}>
                <DialogTrigger asChild><Button variant="outline" data-testid="movement-btn"><ArrowsDownUp size={18} className="mr-1" /> Приймання / Списання</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Рух складу</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Запчастина</Label>
                      <Select value={mv.part_id} onValueChange={(v) => setMv({ ...mv, part_id: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="movement-part"><SelectValue placeholder="Оберіть" /></SelectTrigger>
                        <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.quantity})</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Тип операції</Label>
                      <Select value={mv.type} onValueChange={(v) => setMv({ ...mv, type: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="movement-type"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="in">Приймання (+)</SelectItem><SelectItem value="out">Списання (−)</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Кількість</Label><Input type="number" value={mv.quantity} onChange={(e) => setMv({ ...mv, quantity: e.target.value })} className="mt-1.5" data-testid="movement-qty" /></div>
                    <div><Label>Причина / коментар</Label><Input value={mv.reason} onChange={(e) => setMv({ ...mv, reason: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <DialogFooter><Button onClick={doMovement} data-testid="save-movement-btn">Провести</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyPart); setEditId(null); } }}>
                <DialogTrigger asChild><Button data-testid="add-part-btn"><Plus size={18} className="mr-1" /> Запчастина</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{editId ? "Редагувати" : "Нова запчастина"}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    {F("name", "Назва *")}
                    <div className="grid grid-cols-2 gap-3">
                      {F("sku", "Артикул")}
                      <div className="relative">
                        <Label>Категорія</Label>
                        <Input value={form.category} autoComplete="off"
                          onChange={(e) => { setForm({ ...form, category: e.target.value }); setCatOpen(true); }}
                          onFocus={() => setCatOpen(true)}
                          onBlur={() => setTimeout(() => setCatOpen(false), 150)}
                          placeholder="Оберіть або створіть..." className="mt-1.5" data-testid="part-category" />
                        {catOpen && categories.filter((c) => c.toLowerCase().includes((form.category || "").toLowerCase())).length > 0 && (
                          <div className="absolute z-[60] mt-1 w-full max-h-40 overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="category-options">
                            {categories.filter((c) => c.toLowerCase().includes((form.category || "").toLowerCase())).map((c) => (
                              <button type="button" key={c} data-testid={`cat-opt-${c}`}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                                onMouseDown={() => { setForm((f) => ({ ...f, category: c })); setCatOpen(false); }}>
                                {c}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">{F("quantity", "Кількість", "number")}{F("min_quantity", "Мін. залишок", "number")}
                      <div><Label>Од.</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Закупівля</Label>
                        <div className="flex gap-2 mt-1.5">
                          <Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="flex-1" data-testid="part-cost" />
                          <Select value={form.cost_currency} onValueChange={(v) => setForm({ ...form, cost_currency: v })}>
                            <SelectTrigger className="w-24" data-testid="part-cost-currency"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="UAH">₴ ГРН</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem></SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Продаж</Label>
                        <div className="flex gap-2 mt-1.5">
                          <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="flex-1" data-testid="part-price" />
                          <Select value={form.price_currency} onValueChange={(v) => setForm({ ...form, price_currency: v })}>
                            <SelectTrigger className="w-24" data-testid="part-price-currency"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="UAH">₴ ГРН</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem></SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div><Label>Постачальник</Label>
                      <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter><Button onClick={savePart} data-testid="save-part-btn">Зберегти</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <Card>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Назва</TableHead><TableHead>Артикул</TableHead><TableHead>Категорія</TableHead><TableHead>Залишок</TableHead>
                <TableHead className="text-right">Ціна</TableHead><TableHead className="text-right">Дії</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {visibleParts.map((p) => {
                  const low = p.quantity <= p.min_quantity;
                  return (
                    <TableRow key={p.id} data-testid={`part-row-${p.id}`}>
                      <TableCell className="font-semibold">{p.name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.sku || "—"}</TableCell>
                      <TableCell>{p.category ? <span className="text-xs px-2 py-0.5 rounded bg-secondary">{p.category}</span> : "—"}</TableCell>
                      <TableCell>
                        <span className={`font-semibold ${low ? "text-destructive" : ""}`}>{p.quantity} {p.unit}</span>
                        {low && <span className="ml-2 text-xs text-destructive">мало</span>}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{new Intl.NumberFormat("uk-UA").format(p.price || 0)} {CUR_SYM[p.price_currency] || "₴"}</TableCell>
                      <TableCell className="text-right">
                        <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => editPart(p)} data-testid={`edit-part-${p.id}`}><PencilSimple size={18} /></button>
                        <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => delPart(p.id)} data-testid={`del-part-${p.id}`}><Trash size={18} /></button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {visibleParts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Порожньо</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers">
          <div className="flex justify-end mb-4">
            <Dialog open={supOpen} onOpenChange={setSupOpen}>
              <DialogTrigger asChild><Button data-testid="add-supplier-btn"><Plus size={18} className="mr-1" /> Постачальник</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Новий постачальник</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Назва *</Label><Input value={sup.name} onChange={(e) => setSup({ ...sup, name: e.target.value })} className="mt-1.5" data-testid="supplier-name" /></div>
                  <div><Label>Телефон</Label><Input value={sup.phone} onChange={(e) => setSup({ ...sup, phone: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Контактна особа</Label><Input value={sup.contact} onChange={(e) => setSup({ ...sup, contact: e.target.value })} className="mt-1.5" /></div>
                </div>
                <DialogFooter><Button onClick={saveSup} data-testid="save-supplier-btn">Зберегти</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card>
            <Table>
              <TableHeader><TableRow><TableHead>Назва</TableHead><TableHead>Телефон</TableHead><TableHead>Контакт</TableHead></TableRow></TableHeader>
              <TableBody>
                {suppliers.map((s) => <TableRow key={s.id}><TableCell className="font-semibold">{s.name}</TableCell><TableCell>{s.phone || "—"}</TableCell><TableCell>{s.contact || "—"}</TableCell></TableRow>)}
                {suppliers.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Немає постачальників</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="movements">
          <Card>
            <Table>
              <TableHeader><TableRow><TableHead>Дата</TableHead><TableHead>Запчастина</TableHead><TableHead>Тип</TableHead><TableHead>К-сть</TableHead><TableHead>Причина</TableHead></TableRow></TableHeader>
              <TableBody>
                {movements.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-muted-foreground">{fmtDateTime(m.created_at)}</TableCell>
                    <TableCell className="font-semibold">{m.part_name}</TableCell>
                    <TableCell><span className={m.type === "in" ? "text-green-500" : "text-destructive"}>{m.type === "in" ? "Приймання" : "Списання"}</span></TableCell>
                    <TableCell>{m.type === "in" ? "+" : "−"}{m.quantity}</TableCell>
                    <TableCell className="text-muted-foreground">{m.reason || "—"}</TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Немає операцій</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
